import React, { useEffect, useLayoutEffect, useReducer, useRef } from 'react'

import {
  SCROLL_WRAPPER,
  SCROLL_WRAPPER_THUMB,
  SCROLL_WRAPPER_TRACK,
} from 'utils/testing/testIDs'

import { ActionType, initialState, reducer } from './reducer'
import { ScrollBarThumb, ScrollBarTrack, Wrapper } from './styled-components'

const getTestId = (testId: string, sectionId: string): string => {
  return `${testId}_${sectionId}`
}

const ScrollBarWrapper = ({
  children,
  contentWidth,
  sectionId,
}: {
  children: any
  contentWidth: number
  sectionId: string
}): React.ReactElement => {
  const [
    { barPos, barWidth, isHidden, mouseX, padding, startingPos, touchX },
    dispatch,
  ] = useReducer(reducer, initialState)
  const carouselRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Handle custom scroll bar dimension on window resize
  useLayoutEffect(() => {
    const resizeUpdate = () => {
      if (carouselRef.current) {
        const padding = window
          .getComputedStyle(carouselRef.current)
          .getPropertyValue('padding-left')
          .replace('px', '')
        const paddingVal = Number(padding)
        const wrapperWidth = wrapperRef?.current?.offsetWidth || 0
        const barWidth = (wrapperWidth / (contentWidth + paddingVal * 2)) * 100
        dispatch({
          type: ActionType.Resize,
          payload: {
            barWidth,
            isHidden: barWidth > 100,
            padding: paddingVal,
          },
        })
      }
    }

    if (!padding || !barWidth) {
      resizeUpdate()
    }

    window.addEventListener('resize', resizeUpdate)

    return () => window.removeEventListener('resize', resizeUpdate)
  }, [barWidth, carouselRef, contentWidth, padding])

  // Handle carousel scroll position with custom scroll bar drag
  useEffect(() => {
    const handleReset = () => {
      dispatch({ type: ActionType.BarScrollReset })
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (mouseX !== null) {
        const diff = ((e.pageX - mouseX) / (contentWidth - padding * 2)) * 100
        let nextBarPos = startingPos + diff

        if (nextBarPos < 0) {
          nextBarPos = 0
        } else if (nextBarPos + barWidth > 100) {
          nextBarPos = 100 - barWidth
        }
        dispatch({
          type: ActionType.BarScroll,
          payload: {
            barPos: nextBarPos,
          },
        })
        carouselRef?.current?.scrollTo({
          left: (nextBarPos / 100) * (contentWidth + padding * 2),
        })
      }
    }

    if (mouseX !== null) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleReset)
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (touchX !== null) {
        const diff =
          ((e.touches[0].clientX - touchX) / (contentWidth - padding * 2)) * 100
        let nextBarPos = startingPos + diff

        if (nextBarPos < 0) {
          nextBarPos = 0
        } else if (nextBarPos + barWidth > 100) {
          nextBarPos = 100 - barWidth
        }
        dispatch({
          type: ActionType.BarScroll,
          payload: {
            barPos: nextBarPos,
          },
        })
        carouselRef?.current?.scrollTo({
          left: (nextBarPos / 100) * (contentWidth + padding * 2),
        })
      }
    }

    if (touchX !== null) {
      window.addEventListener('touchend', handleReset)
      window.addEventListener('touchmove', handleTouchMove)
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleReset)
      window.removeEventListener('touchmove', handleTouchMove)
    }
  }, [barPos, barWidth, contentWidth, mouseX, padding, startingPos, touchX])

  // Set initial mouse position for scroll bar drag
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    dispatch({
      type: ActionType.BarScrollInit,
      payload: {
        mouseX: e.clientX,
        startingPos: barPos,
      },
    })
  }

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    dispatch({
      type: ActionType.BarScrollInit,
      payload: {
        startingPos: barPos,
        touchX: e.touches[0].clientX,
      },
    })
  }

  // Handle custom scroll bar position on carousel scroll
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    dispatch({
      type: ActionType.CarouselScroll,
      payload: {
        barPos:
          (e.currentTarget.scrollLeft / (contentWidth + padding * 2)) * 100,
      },
    })
  }

  if (React.Children.count(children) !== 1) {
    throw new Error('Must have 1 child only')
  }

  return (
    <Wrapper
      data-testid={getTestId(SCROLL_WRAPPER, sectionId)}
      isHidden={isHidden}
      ref={wrapperRef}
    >
      {React.Children.map(children, (child) => {
        return React.cloneElement(child, {
          onScroll: handleScroll,
          ref: carouselRef,
        })
      })}
      <ScrollBarTrack
        data-testid={getTestId(SCROLL_WRAPPER_TRACK, sectionId)}
        isHidden={isHidden}
        padding={padding}
      >
        <ScrollBarThumb
          barPos={barPos}
          barWidth={barWidth}
          data-testid={getTestId(SCROLL_WRAPPER_THUMB, sectionId)}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        />
      </ScrollBarTrack>
    </Wrapper>
  )
}

export { ScrollBarWrapper }
